import React, { useEffect, useRef } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';

interface SwipeBackGestureProps {
  children: React.ReactNode;
  enabled?: boolean;
  swipeThreshold?: number;
}

const SwipeBackGesture: React.FC<SwipeBackGestureProps> = ({
  children,
  enabled = true,
  swipeThreshold = 100
}) => {
  const { goBack, navigationState } = useNavigation();
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled || !navigationState.canGoBack) return;

    const container = containerRef.current;
    if (!container) return;

    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    const handleTouchStart = (e: TouchEvent) => {
      // Only trigger on edge swipes (within 50px of left edge)
      if (e.touches[0].clientX > 50) return;
      
      startX = e.touches[0].clientX;
      startXRef.current = startX;
      isDragging = false;
      isDraggingRef.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (startX === 0) return;
      
      currentX = e.touches[0].clientX;
      const deltaX = currentX - startX;
      
      // Only consider rightward swipes
      if (deltaX < 0) return;
      
      if (deltaX > 10 && !isDragging) {
        isDragging = true;
        isDraggingRef.current = true;
        
        // Prevent scrolling while swiping
        e.preventDefault();
        
        // Add visual feedback (optional)
        container.style.transform = `translateX(${Math.min(deltaX * 0.3, 30)}px)`;
        container.style.transition = 'none';
      } else if (isDragging) {
        e.preventDefault();
        container.style.transform = `translateX(${Math.min(deltaX * 0.3, 30)}px)`;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (startX === 0) return;
      
      const endX = e.changedTouches[0].clientX;
      const deltaX = endX - startX;
      
      // Reset visual state
      if (container) {
        container.style.transform = '';
        container.style.transition = 'transform 0.3s ease-out';
      }
      
      // Trigger navigation if swipe threshold is met
      if (deltaX > swipeThreshold && isDragging) {
        goBack();
      }
      
      // Reset tracking variables
      startX = 0;
      startXRef.current = 0;
      isDragging = false;
      isDraggingRef.current = false;
    };

    // Mouse events for desktop testing (optional)
    const handleMouseDown = (e: MouseEvent) => {
      if (e.clientX > 50) return;
      startX = e.clientX;
      isDragging = false;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (startX === 0 || !e.buttons) return;
      
      currentX = e.clientX;
      const deltaX = currentX - startX;
      
      if (deltaX > 10 && !isDragging) {
        isDragging = true;
        container.style.transform = `translateX(${Math.min(deltaX * 0.3, 30)}px)`;
        container.style.transition = 'none';
      } else if (isDragging) {
        container.style.transform = `translateX(${Math.min(deltaX * 0.3, 30)}px)`;
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (startX === 0) return;
      
      const deltaX = e.clientX - startX;
      
      if (container) {
        container.style.transform = '';
        container.style.transition = 'transform 0.3s ease-out';
      }
      
      if (deltaX > swipeThreshold && isDragging) {
        goBack();
      }
      
      startX = 0;
      isDragging = false;
    };

    // Add event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    // Optional: Enable for desktop testing
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
    };
  }, [enabled, goBack, navigationState.canGoBack, swipeThreshold]);

  return (
    <div ref={containerRef} className="w-full h-full">
      {children}
    </div>
  );
};

export default SwipeBackGesture;